/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import classnames from 'classnames';
import { momentObj } from 'react-moment-proptypes';
import { themeable } from '@instructure/ui-themeable';
import { ToggleDetails } from '@instructure/ui-toggle-details';
import { Pill } from '@instructure/ui-elements';
import { func, number, string, arrayOf, shape, oneOf } from 'prop-types';
import BadgeList from '../BadgeList';
import NotificationBadge, { MissingIndicator, NewActivityIndicator } from '../NotificationBadge';
import { badgeShape, sizeShape } from '../plannerPropTypes';
import { animatable } from '../../dynamic-ui';
const styles = {
  componentId: 'ekpsc',
  template: function template(theme) {
    return "\n\n.CompletedItemsFacade-styles__root {\n  display: flex;\n  flex:1;\n  align-items: center;\n  font-family: ".concat(theme.fontFamily || 'inherit', ";\n  color: ").concat(theme.color || 'inherit', ";\n  box-sizing: border-box;\n  padding: ").concat(theme.padding || 'inherit', ";\n  border-bottom: ").concat(theme.borderWidth || 'inherit', " solid ").concat(theme.borderColor || 'inherit', ";\n}\n\n.CompletedItemsFacade-styles__activityIndicator {\n  -webkit-padding-end: 0;\n          padding-inline-end: 0;\n  -webkit-padding-start: 0;\n          padding-inline-start: 0;\n}\n\n[dir=\"ltr\"] .CompletedItemsFacade-styles__activityIndicator {\n  padding-right: 0;\n  padding-left: 0;\n}\n\n[dir=\"rtl\"] .CompletedItemsFacade-styles__activityIndicator {\n  padding-left: 0;\n  padding-right: 0;\n}\n\n.CompletedItemsFacade-styles__showLabel {\n  -webkit-margin-start: ").concat(theme.gutterWidth || 'inherit', ";\n          margin-inline-start: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n[dir=\"ltr\"] .CompletedItemsFacade-styles__showLabel {\n  margin-left: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n[dir=\"rtl\"] .CompletedItemsFacade-styles__showLabel {\n  margin-right: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n.CompletedItemsFacade-styles__contentPrimary {\n  flex: 0 0 50%;\n  margin-bottom: 0;\n  -webkit-margin-start: ").concat(theme.gutterWidth || 'inherit', ";\n          margin-inline-start: ").concat(theme.gutterWidth || 'inherit', ";\n  box-sizing: border-box;\n  min-width: 1px;\n}\n\n[dir=\"ltr\"] .CompletedItemsFacade-styles__contentPrimary {\n  margin-left: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n[dir=\"rtl\"] .CompletedItemsFacade-styles__contentPrimary {\n  margin-right: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n.CompletedItemsFacade-styles__contentSecondary {\n  flex: 1 0;\n  justify-content: flex-end;\n  box-sizing: border-box;\n  min-width: 1px;\n  text-align: end;\n}\n\n[dir=\"ltr\"] .CompletedItemsFacade-styles__contentSecondary {\n  text-align: right;\n}\n\n[dir=\"rtl\"] .CompletedItemsFacade-styles__contentSecondary {\n  text-align: left;\n}\n\n.CompletedItemsFacade-styles__activityIndicator + .CompletedItemsFacade-styles__contentPrimary {\n  -webkit-margin-start: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.buttonPadding || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ");\n          margin-inline-start: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.buttonPadding || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ");\n}\n\n[dir=\"ltr\"] .CompletedItemsFacade-styles__activityIndicator + .CompletedItemsFacade-styles__contentPrimary {\n  margin-left: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.buttonPadding || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ");\n}\n\n[dir=\"rtl\"] .CompletedItemsFacade-styles__activityIndicator + .CompletedItemsFacade-styles__contentPrimary {\n  margin-right: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.buttonPadding || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ");\n}\n\n.CompletedItemsFacade-styles__small {\n  padding-left: 0;\n  padding-right: 0;\n}\n\n.CompletedItemsFacade-styles__small .CompletedItemsFacade-styles__contentPrimary {\n    -webkit-margin-start: 10px;\n            margin-inline-start: 10px;\n    flex-basis: auto;\n  }\n\n[dir=\"ltr\"] .CompletedItemsFacade-styles__small .CompletedItemsFacade-styles__contentPrimary {\n    margin-left: 10px;\n  }\n\n[dir=\"rtl\"] .CompletedItemsFacade-styles__small .CompletedItemsFacade-styles__contentPrimary {\n    margin-right: 10px;\n  }\n\n.CompletedItemsFacade-styles__small .CompletedItemsFacade-styles__contentSecondary {\n    display: none;\n  }\n");
  },
  'root': 'CompletedItemsFacade-styles__root',
  'activityIndicator': 'CompletedItemsFacade-styles__activityIndicator',
  'showLabel': 'CompletedItemsFacade-styles__showLabel',
  'contentPrimary': 'CompletedItemsFacade-styles__contentPrimary',
  'contentSecondary': 'CompletedItemsFacade-styles__contentSecondary',
  'small': 'CompletedItemsFacade-styles__small'
};
import theme from './theme.js';
import formatMessage from '../../format-message';
export class CompletedItemsFacade extends Component {
  constructor() {
    super(...arguments);

    this.getFocusable = () => {
      return this.buttonRef;
    };
  }

  componentDidMount() {
    this.props.registerAnimatable('item', this, this.props.animatableIndex, this.props.animatableItemIds);
  }

  componentWillReceiveProps(newProps) {
    this.props.deregisterAnimatable('item', this, this.props.animatableItemIds);
    this.props.registerAnimatable('item', this, newProps.animatableIndex, newProps.animatableItemIds);
  }

  componentWillUnmount() {
    this.props.deregisterAnimatable('item', this, this.props.animatableItemIds);
  }

  getScrollable() {
    return this.rootDiv;
  }

  renderBadges() {
    if (this.props.badges.length) {
      return React.createElement(BadgeList, null, this.props.badges.map(b => React.createElement(Pill, {
        key: b.id,
        text: b.text,
        variant: b.variant
      })));
    }

    return null;
  }

  renderNotificationBadge() {
    if (this.props.notificationBadge === 'none') return React.createElement(NotificationBadge, {
      responsiveSize: this.props.responsiveSize
    });
    const isNewItem = this.props.notificationBadge === 'newActivity';
    const IndicatorComponent = isNewItem ? NewActivityIndicator : MissingIndicator;
    const badgeMessage = formatMessage('{items} completed {items, plural,=1 {item} other {items}}', {
      items: this.props.itemCount
    });
    return React.createElement(NotificationBadge, {
      responsiveSize: this.props.responsiveSize
    }, React.createElement("div", {
      className: styles.activityIndicator
    }, React.createElement(IndicatorComponent, {
      title: badgeMessage,
      itemIds: this.props.animatableItemIds,
      animatableIndex: this.props.animatableIndex,
      getFocusable: this.getFocusable
    })));
  }

  render() {
    const theme = this.theme ? {
      textColor: this.theme.labelColor,
      iconColor: this.theme.labelColor,
      iconMargin: this.theme.gutterWidth
    } : null;
    return React.createElement("div", {
      className: classnames(styles.root, styles[this.props.responsiveSize], 'planner-completed-items'),
      ref: elt => this.rootDiv = elt
    }, this.renderNotificationBadge(), React.createElement("div", {
      className: styles.contentPrimary
    }, React.createElement(ToggleDetails, {
      ref: _ref => this.buttonRef = _ref,
      onToggle: this.props.onClick,
      summary: formatMessage("{\n                  count, plural,\n                  one {Show # completed item}\n                  other {Show # completed items}\n                }", {
        count: this.props.itemCount
      }),
      theme: theme
    }, "ToggleDetails requires a child")), React.createElement("div", {
      className: styles.contentSecondary
    }, this.renderBadges()));
  }

}
CompletedItemsFacade.propTypes = {
  onClick: func.isRequired,
  itemCount: number.isRequired,
  badges: arrayOf(shape(badgeShape)),
  animatableIndex: number,
  animatableItemIds: arrayOf(string),
  registerAnimatable: func,
  deregisterAnimatable: func,
  notificationBadge: oneOf(['none', 'newActivity', 'missing']),
  date: momentObj,
  // the scroll-to-today animation requires a date on each component in the planner
  responsiveSize: sizeShape
};
CompletedItemsFacade.defaultProps = {
  badges: [],
  registerAnimatable: () => {},
  deregisterAnimatable: () => {},
  notificationBadge: 'none',
  responsiveSize: 'large'
};
export default animatable(themeable(theme, styles)(CompletedItemsFacade));